// deployment_class: customer_env_allowed
/**
 * useApplyPreferences — applies the user's VISUAL preferences to the live document
 * (User Preferences spec §7.2). Mounted once in the app shell so a saved font-scale
 * / density / reduced-motion preference takes effect app-wide and updates instantly
 * when the user changes it on the Settings screen.
 *
 *   visual.font_scale     → CSS var `--font-scale-multiplier` on `.kathedral-frame`
 *                           (scales the root font-size; all rem sizes follow)
 *   visual.density        → CSS var `--density-mode`
 *   visual.reduced_motion → `data-reduced-motion="true|false"` attribute
 *
 * The target is `document.documentElement` (the <html class="kathedral-frame"> element).
 */
import { useEffect } from 'react';
import { usePreferences, effectiveReducedMotion, effectiveTheme } from './usePreferences';

const FONT_SCALE_VALUE: Record<string, string> = {
  '1.0': '1',
  '1.15': '1.15',
  '1.3': '1.3',
};

// Defense-in-depth (break-it hardening 2026-07-14). The backend now REJECTS
// out-of-domain visual values at the PATCH boundary, so nothing invalid should
// persist — but a legacy row or a direct DB write could still carry a hostile
// value, and an unclamped value here paints a broken render (negative zoom, an
// alpha of 5.0, a 404 backdrop). These guards keep every projected value in its
// declared domain no matter what reaches the hook.
const ALLOWED_BACKDROPS = ['meadow', 'hills', 'sunrise', 'bigsky', 'dusk', 'exec', 'dawn'];
function clampRange(v: unknown, min: number, max: number, fallback: number): number {
  const n = typeof v === 'number' && Number.isFinite(v) ? v : fallback;
  return Math.max(min, Math.min(max, n));
}

export function useApplyPreferences(): void {
  const prefs = usePreferences();
  const { font_scale, density, high_contrast, colorblind_safe, dyslexia_font, large_targets,
    dashboard_backdrop_opacity, dashboard_backdrop, module_opacity, module_definition,
    drill_panel_opacity, text_scale } = prefs.visual;
  // effectiveReducedMotion / effectiveTheme resolve 'auto' / 'system' against the
  // OS → stable values we can depend on directly.
  const reducedMotion = effectiveReducedMotion(prefs);
  const theme = effectiveTheme(prefs);

  useEffect(() => {
    const root = document.documentElement;
    // Text size has two layers: the a11y PRESET (font_scale, load-bearing
    // low-vision) and the fine-tune (text_scale). The PRESET scales the root
    // font-size so every rem-based surface (the shell + Settings) follows.
    const presetMult = Number(FONT_SCALE_VALUE[font_scale] ?? '1');
    root.style.setProperty('--font-scale-multiplier', String(presetMult));
    // The dashboard (.kdash) + the drill are PX-based, so the root font-scale
    // NEVER reaches them — they must zoom instead. --kd-content-scale is the FULL
    // effective content size = PRESET × fine, so BOTH the "Large" preset (E3
    // Large_Text_Bug: preset must scale the workspace, not just the shell) AND the
    // fine slider scale the workspace + drill. The fine-tune is kept OUT of the
    // root scale so it never moves Settings (E3 Default_UI_Settings_1c).
    root.style.setProperty('--kd-content-scale', String(presetMult * (clampRange(text_scale, 80, 150, 100) / 100)));
    root.style.setProperty('--density-mode', density);
    root.setAttribute('data-density', density);
    // Ultra Compact ALSO zooms the workspace (2/3). Expose it as a FACTOR so it
    // COMPOSES with the text scale in one zoom (.kd-work zoom = content × density).
    // Otherwise the density zoom and the text-scale zoom fight over the same
    // `zoom` property and density silently clobbers "Large" (Large_Text_Bug).
    root.style.setProperty('--kd-density-scale', density === 'ultra_compact' ? '0.667' : '1');
    // Dashboard backdrop opacity (0–100 → 0..1). All dashboards read --kd-dash-bg.
    root.style.setProperty('--kd-dash-bg', String(clampRange(dashboard_backdrop_opacity, 0, 100, 25) / 100));
    // Dashboard backdrop image — the dashboards + account pages read --kd-dash-bg-url.
    // Files live under public/dashboard-backdrops/<id>.jpg (served at /dashboard-backdrops/).
    // Guard the id against the known set so a hostile value can't 404 the backdrop.
    const backdrop = ALLOWED_BACKDROPS.includes(dashboard_backdrop) ? dashboard_backdrop : 'meadow';
    root.style.setProperty(
      '--kd-dash-bg-url',
      `url('/dashboard-backdrops/${backdrop}.jpg')`,
    );
    // Module surfaces — fill opacity (0..1) + edge definition (0..1). Dashboards
    // + account panels read --kd-mod-alpha / --kd-mod-def.
    root.style.setProperty('--kd-mod-alpha', String(clampRange(module_opacity, 0, 100, 45) / 100));
    root.style.setProperty('--kd-mod-def', String(clampRange(module_definition, 0, 100, 50) / 100));
    // Detail slide-over ("glass door") panel opacity — .kd-drill reads --kd-drill-alpha.
    root.style.setProperty('--kd-drill-alpha', String(clampRange(drill_panel_opacity, 0, 100, 82) / 100));
    root.setAttribute('data-reduced-motion', reducedMotion ? 'true' : 'false');
    // Theme — the token set lives under [data-theme] in tokens.css.
    root.setAttribute('data-theme', theme);
    // Accessibility flags — each toggles a token/treatment block in tokens.css.
    root.toggleAttribute('data-high-contrast', high_contrast);
    root.toggleAttribute('data-colorblind-safe', colorblind_safe);
    root.toggleAttribute('data-dyslexia-font', dyslexia_font);
    root.toggleAttribute('data-large-targets', large_targets);
  }, [
    font_scale,
    density,
    reducedMotion,
    theme,
    high_contrast,
    colorblind_safe,
    dyslexia_font,
    large_targets,
    dashboard_backdrop_opacity,
    dashboard_backdrop,
    module_opacity,
    module_definition,
    drill_panel_opacity,
    text_scale,
  ]);
}
